import { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Alert, Stack, Chip, CircularProgress, Paper, Link, Tooltip,
  TextField, InputAdornment, Table, TableBody, TableCell, TableHead, TableRow,
  IconButton, Snackbar,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// FEAT-019 — 예약 실행(cron) 스케줄 화면.
//
// 🔴 이 화면의 목적은 현황 표시가 아니라 **자동화를 걸 시간대를 고르게 하는 것**이다.
//   혼잡은 한 사람이 만든 게 아니라 아무도 전체를 볼 수 없어서 쌓인 결과다. 그래서
//   "지금 이렇습니다" 에서 멈추지 않고 "여기에 거세요" 까지 간다 — 표를 읽고 스스로
//   판단하라고 하면 대부분 읽지 않고 하던 대로 정각을 적는다.
//
// 🔴 꺼진 워크플로우는 혼잡 수치에 넣지 않는다.
//   정의가 있어도 꺼져 있으면 자원을 쓰지 않는다. 섞으면 화면이 틀린 말을 한다.
//   다만 목록에서는 꺼짐으로 구분해 보여준다 — 없는 것과 꺼진 것은 다르다.
//
// 데이터: GET /api/actions/schedule (6시간마다 수집되는 스냅샷)

const KST_OFFSET = 9;
const pad = (n) => String(n).padStart(2, '0');
const toKstHour = (utcHour) => (utcHour + KST_OFFSET) % 24;
const toUtcHour = (kstHour) => (kstHour - KST_OFFSET + 24) % 24;
const hhmm = (h, m) => `${pad(h)}:${pad(m)}`;

// 하루 24회를 넘는 것(분 단위 반복)은 격자에 넣지 않는다 — 한 줄이 모든 칸을
// 채워 나머지를 읽을 수 없게 만든다. 별도로 센다.
const isHighFreq = (e) => (e.times?.length || 0) > 24;

const DAYS = ['일', '월', '화', '수', '목', '금', '토'];

const STATE_META = {
  active: { label: '활성', color: 'success' },
  disabled_manually: { label: '수동 비활성', color: 'default' },
  disabled_inactivity: { label: '자동 중단', color: 'warning' },
  unregistered: { label: '미등록', color: 'warning' },
};

// 혼잡도 → 색. 강조색과 분리된 의미 색이다.
// 절대 건수가 아니라 **그 격자의 최대값 대비**로 칠한다. 요일로 쪼개면 칸당 수가
// 작아지는데 절대 임계값을 쓰면 전부 옅은 색이 되어 쏠림이 안 보인다.
function heatColor(n, max) {
  if (!n) return 'transparent';
  const r = n / (max || 1);
  if (r <= 0.12) return 'rgba(63,185,80,0.28)';
  if (r <= 0.3) return 'rgba(63,185,80,0.55)';
  if (r <= 0.5) return 'rgba(210,153,34,0.65)';
  if (r <= 0.75) return 'rgba(219,109,40,0.82)';
  return 'rgba(198,69,61,0.92)';
}

export default function ActionsSchedulePage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [hint, setHint] = useState(null);
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState(null); // 주간 격자에서 고른 칸 `${요일}:${시}`
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/actions/schedule')
      .then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw Object.assign(new Error(body.error || `HTTP ${r.status}`), { hint: body.hint });
        return body;
      })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) { setErr(e.message); setHint(e.hint || null); } });
    return () => { cancelled = true; };
  }, []);

  const entries = data?.entries || [];
  const active = useMemo(() => entries.filter((e) => e.state === 'active' && !isHighFreq(e)), [entries]);
  const highFreq = useMemo(() => entries.filter(isHighFreq), [entries]);

  // KST 시:분 → 그 시각에 출발하는 활성 워크플로우
  const slots = useMemo(() => {
    const map = new Map();
    for (const e of active) {
      for (const [uh, m] of e.times) {
        const key = toKstHour(uh) * 60 + m;
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(e);
      }
    }
    return map;
  }, [active]);

  // 🔴 주간 격자 — 요일(일~토) × 시(KST).
  //   요일을 보지 않고 하루 격자만 그리면 주간 워크플로우(실측 65건)가 매일 도는 것처럼
  //   세어져 평일 혼잡을 과장한다. 그리고 UTC→KST 로 옮길 때 23시대는 **요일이 하루
  //   밀린다** (`0 23 * * 0` 일 23:00 UTC = 월 08:00 KST). 이걸 놓치면 격자가 틀린다.
  //
  //   월간(특정 일자) 워크플로우는 그 날짜의 요일이 매달 달라 어느 칸에도 고정할 수
  //   없다. 7칸에 모두 넣으면 과장이므로 격자에서 빼고 건수만 알린다.
  const weekly = useMemo(() => {
    const cells = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => []));
    for (const e of active) {
      if (e.dom) continue;
      for (const [uh, m] of e.times) {
        const kh = toKstHour(uh);
        const dayShift = uh + KST_OFFSET >= 24 ? 1 : 0;
        // 요일 지정이 없으면 매일 — 날짜 이동과 무관하게 7일 전부.
        const days = e.dow ? e.dow.map((d) => (d + dayShift) % 7) : [0, 1, 2, 3, 4, 5, 6];
        for (const d of days) cells[d][kh].push({ e, m });
      }
    }
    return cells;
  }, [active]);

  const monthlyCount = useMemo(() => active.filter((e) => e.dom).length, [active]);
  const weeklyMax = useMemo(
    () => Math.max(1, ...weekly.flat().map((c) => c.length)),
    [weekly],
  );

  // KST 시간대별 총 발사 수
  const hourLoad = useMemo(() => {
    const arr = new Array(24).fill(0);
    for (const [key, list] of slots) arr[Math.floor(key / 60)] += list.length;
    return arr;
  }, [slots]);

  // 🔴 추천 시간대 — 이 화면의 핵심 산출물.
  //   ① 아무도 쓰지 않는 분을 고른다 (다른 사람과 겹치지 않게 서로 다른 분을 배정)
  //   ② 발사가 적은 시간대 순으로 배치
  //   ③ cron 은 UTC 로 준다 — 그대로 붙여 넣어야 맞는다. 사람이 환산하게 하면
  //      환산 실수로 결국 정각에 걸린다.
  const suggestions = useMemo(() => {
    if (active.length === 0) return [];
    const used = new Set(active.flatMap((e) => e.times.map(([, m]) => m)));
    const freeMinutes = [...Array(60).keys()].filter((m) => !used.has(m) && m > 3 && m < 57);
    if (freeMinutes.length === 0) return [];
    const quietHours = [...Array(24).keys()].sort((a, b) => hourLoad[a] - hourLoad[b] || a - b).slice(0, 6);
    return quietHours.map((kh, i) => {
      // 추천마다 다른 분을 배정 — 모두 같은 분을 받으면 새 혼잡을 만든다.
      const minute = freeMinutes[(i * Math.max(1, Math.floor(freeMinutes.length / 6))) % freeMinutes.length];
      return {
        kstHour: kh,
        minute,
        utcHour: toUtcHour(kh),
        load: hourLoad[kh],
        cron: `${minute} ${toUtcHour(kh)} * * *`,
      };
    });
  }, [active, hourLoad]);

  const jams = useMemo(
    () => [...slots.entries()].filter(([, v]) => v.length >= 2).sort((a, b) => b[1].length - a[1].length),
    [slots],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = entries.map((e) => {
      const first = e.times?.length ? [...e.times].sort((a, b) => a[0] * 60 + a[1] - (b[0] * 60 + b[1]))[0] : null;
      const kst = first ? [toKstHour(first[0]), first[1]] : null;
      return { ...e, kstLabel: isHighFreq(e) ? '상시' : kst ? hhmm(kst[0], kst[1]) + (e.times.length > 1 ? ` +${e.times.length - 1}` : '') : '—', sortKey: kst ? kst[0] * 60 + kst[1] : 9999 };
    });
    const out = q
      ? rows.filter((e) => `${e.repo} ${e.file} ${e.name} ${e.cron}`.toLowerCase().includes(q))
      : rows;
    return out.sort((a, b) => a.sortKey - b.sortKey || a.repo.localeCompare(b.repo));
  }, [entries, search]);

  const copy = (text) => {
    navigator.clipboard?.writeText(text).then(
      () => setCopied(text),
      () => setCopied(null),
    );
  };

  // 고른 칸의 상세 — 분 분포 + 목록
  const pickedCell = useMemo(() => {
    if (!picked) return null;
    const [d, h] = picked.split(':').map(Number);
    const items = weekly[d]?.[h] || [];
    if (items.length === 0) return null;
    const byMinute = [...items.reduce((map, it) => {
      if (!map.has(it.m)) map.set(it.m, []);
      map.get(it.m).push(it);
      return map;
    }, new Map())].sort((a, b) => a[0] - b[0]);
    return { d, h, items, byMinute };
  }, [picked, weekly]);

  if (err) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <Typography variant="body2" fontWeight={600}>{err}</Typography>
          {hint && <Typography variant="caption">{hint}</Typography>}
        </Alert>
      </Box>
    );
  }
  if (!data) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;



  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
        <Typography variant="h5" fontWeight={700}>예약 실행 스케줄</Typography>
        <Typography variant="caption" color="text.secondary">
          {data.org} · 저장소 {data.scanned_repos} · 예약 {entries.length}건 ({new Set(entries.map((e) => e.repo)).size}개 저장소)
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        자동화를 새로 걸기 전에 <b>비어 있는 요일·시간대</b>를 고르는 화면입니다. 시각은 모두 <b>KST</b>, cron 은 UTC 입니다.
        {' · '}갱신 {data.generated_at ? new Date(data.generated_at).toLocaleString('ko-KR', { hour12: false }) : '—'} (6시간마다) ·{' '}
        <Link href={`https://github.com/${data.sourceRepo}/blob/main/${data.sourcePath}`} target="_blank" rel="noreferrer">
          수집 결과 <OpenInNewIcon sx={{ fontSize: 11, verticalAlign: 'middle' }} />
        </Link>
      </Typography>

      {/* ── 추천 시간대 — 이 화면에 온 이유 ───────────────────────── */}
      <Paper variant="outlined" sx={{ borderRadius: 0, p: 2, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>여기에 거세요 — 비어 있는 시간대</Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          아무도 쓰지 않는 분(分)에, 발사가 적은 시간대로 배치했습니다. 추천마다 분을 다르게 잡아 서로 겹치지 않습니다.
          매일 1회 기준이며, cron 은 그대로 붙여 넣으면 됩니다.
        </Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          {suggestions.map((s) => (
            <Paper key={s.cron} variant="outlined" sx={{ borderRadius: 0, px: 1.5, py: 1, minWidth: 210 }}>
              <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {hhmm(s.kstHour, s.minute)} KST
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace', color: 'text.secondary' }}>
                  {s.cron}
                </Typography>
                <Tooltip title="cron 복사" arrow>
                  <IconButton size="small" onClick={() => copy(s.cron)} aria-label={`${s.cron} 복사`}>
                    <ContentCopyIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Typography variant="caption" color={s.load === 0 ? 'success.main' : 'text.secondary'}>
                이 시간대 현재 {s.load}건
              </Typography>
            </Paper>
          ))}
          {suggestions.length === 0 && (
            <Typography variant="caption" color="text.secondary">추천할 빈 자리를 찾지 못했습니다.</Typography>
          )}
        </Stack>
      </Paper>

      {/* ── 주간 격자 ─────────────────────────────────────────────── */}
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>주간 격자 — 요일 × 시 (KST)</Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        칸의 수는 그 요일·그 시간대에 출발하는 활성 워크플로우 수입니다.
        <b> 칸을 누르면 몇 분에 몰려 있는지와 어떤 저장소인지 펼쳐집니다.</b>
        {monthlyCount > 0 && ` · 월간(특정 일자) ${monthlyCount}건은 요일이 매달 달라 격자에서 제외했습니다.`}
      </Typography>
      <Paper variant="outlined" sx={{ borderRadius: 0, overflowX: 'auto', mb: 1.5 }}>
        <Table size="small" sx={{ minWidth: 460 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 62, fontSize: 11, color: 'text.secondary' }} />
              {DAYS.map((d, i) => (
                <TableCell
                  key={d}
                  align="center"
                  sx={{
                    fontSize: 12, fontWeight: 700, px: 0.5,
                    color: i === 0 ? 'error.main' : i === 6 ? 'primary.main' : 'text.secondary',
                  }}
                >
                  {d}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {[...Array(24).keys()].map((h) => (
              <TableRow key={h}>
                <TableCell sx={{ fontSize: 12, color: 'text.secondary', fontFamily: 'JetBrains Mono, monospace', py: 0.25 }}>
                  {pad(h)}시
                </TableCell>
                {DAYS.map((_, d) => {
                  const cell = weekly[d][h];
                  const n = cell.length;
                  const key = `${d}:${h}`;
                  return (
                    <TableCell
                      key={key}
                      align="center"
                      onClick={n ? () => setPicked(picked === key ? null : key) : undefined}
                      sx={{
                        p: 0, height: 26, cursor: n ? 'pointer' : 'default',
                        bgcolor: heatColor(n, weeklyMax),
                        fontFamily: 'JetBrains Mono, monospace', fontSize: 12, fontWeight: 700,
                        color: n / weeklyMax >= 0.5 ? '#fff' : 'text.primary',
                        outline: picked === key ? '2px solid' : 'none',
                        outlineColor: 'primary.main', outlineOffset: '-2px',
                      }}
                    >
                      {n || ''}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {pickedCell && (
        <Paper variant="outlined" sx={{ borderRadius: 0, p: 1.5, mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            {DAYS[pickedCell.d]}요일 {pad(pickedCell.h)}시 — {pickedCell.items.length}건
          </Typography>
          {/* 🔴 분 분포 — 시 단위로만 보면 '정각 쏠림' 이 사라진다.
              같은 시간대 안에서도 :00 에 몰려 있는지 흩어져 있는지가 실제 혼잡을 가른다. */}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            분 분포:{' '}
            {pickedCell.byMinute.map(([m, list]) => (
              <Box
                component="span"
                key={m}
                sx={{ mr: 1, fontFamily: 'JetBrains Mono, monospace', color: m === 0 ? 'error.main' : 'inherit', fontWeight: m === 0 ? 700 : 400 }}
              >
                :{pad(m)} {list.length}건
              </Box>
            ))}
          </Typography>
          <Box sx={{ display: 'grid', gap: 0.5, gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
            {pickedCell.items
              .slice()
              .sort((a, b) => a.m - b.m || a.e.repo.localeCompare(b.e.repo))
              .map(({ e, m }) => (
                <Typography key={`${e.full}/${e.file}/${m}`} variant="caption" sx={{ color: 'text.secondary' }}>
                  <Box component="span" sx={{ fontFamily: 'JetBrains Mono, monospace', mr: 0.5 }}>:{pad(m)}</Box>
                  <b style={{ color: 'inherit' }}>{e.repo}</b>{' '}
                  <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{e.file}</span>
                </Typography>
              ))}
          </Box>
        </Paper>
      )}

      {/* ── 혼잡 구간 ─────────────────────────────────────────────── */}
      {jams.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>혼잡 구간 — 같은 분에 둘 이상</Typography>
          <Paper variant="outlined" sx={{ borderRadius: 0 }}>
            {jams.slice(0, 10).map(([key, list]) => {
              const h = Math.floor(key / 60);
              const m = key % 60;
              const repos = [...new Set(list.map((x) => x.repo))];
              return (
                <Box key={key} sx={{ display: 'flex', gap: 1.5, px: 1.5, py: 0.9, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, minWidth: 56 }}>
                    {hhmm(h, m)}
                  </Typography>
                  <Typography variant="body2" fontWeight={700} color={list.length >= 10 ? 'error.main' : 'text.primary'} sx={{ minWidth: 44 }}>
                    {list.length}건
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 200 }}>
                    {repos.slice(0, 7).join(', ')}{repos.length > 7 ? ` 외 ${repos.length - 7}개` : ''}
                  </Typography>
                </Box>
              );
            })}
          </Paper>
        </Box>
      )}

      {/* ── 전수 목록 ─────────────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" fontWeight={700}>전체 목록</Typography>
        <TextField
          size="small"
          placeholder="저장소 · 워크플로우 · cron 검색"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: { xs: '100%', sm: 320 } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <Typography variant="caption" color="text.secondary">
          {filtered.length} / {entries.length}건
          {highFreq.length > 0 && ` · 고빈도 ${highFreq.length}건은 격자 제외`}
        </Typography>
      </Stack>
      <Paper variant="outlined" sx={{ borderRadius: 0, overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 760 }}>
          <TableHead>
            <TableRow>
              <TableCell>KST</TableCell>
              <TableCell>주기</TableCell>
              <TableCell>저장소</TableCell>
              <TableCell>워크플로우</TableCell>
              <TableCell>cron (UTC)</TableCell>
              <TableCell align="right">30일</TableCell>
              <TableCell>상태</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((e) => {
              const st = STATE_META[e.state] || { label: e.state, color: 'default' };
              return (
                <TableRow key={`${e.full}/${e.file}/${e.cron}`} hover>
                  <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', whiteSpace: 'nowrap' }}>{e.kstLabel}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{e.freq}</TableCell>
                  <TableCell>{e.repo}</TableCell>
                  <TableCell>
                    <Link href={e.url} target="_blank" rel="noreferrer" sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>
                      {e.file}
                    </Link>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', whiteSpace: 'nowrap' }}>{e.cron}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>{e.runs30d ?? '—'}</TableCell>
                  <TableCell>
                    <Chip label={st.label} size="small" color={st.color} variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <Snackbar
        open={Boolean(copied)}
        autoHideDuration={2000}
        onClose={() => setCopied(null)}
        message={copied ? `복사했습니다 — ${copied}` : ''}
      />
    </Box>
  );
}
